import React, { useState } from 'react';
import { Loader2, Lock, PencilLine, Search, Trash2 } from 'lucide-react';
import { SifaTaetigkeit } from '../../services/sifa/betreuungTypes';
import { formatDauer } from '../../services/sifa/taetigkeitenLogic';
import { formatDatum } from './unterweisungModulLogic';
import { artLabel, darfLoeschen, sucheTaetigkeiten } from './taetigkeitenModulLogic';

interface Props {
  taetigkeiten: SifaTaetigkeit[];
  laden: boolean;
  jahr: number;
  isReadOnly?: boolean;
  isMobile?: boolean;
  onBearbeiten: (t: SifaTaetigkeit) => void;
  onLoeschen: (t: SifaTaetigkeit) => void;
}

/**
 * Die Taetigkeiten eines Jahres als Tabelle (Handy: Karten): Datum, Art,
 * Titel, Dauer, Wer. Abgerechnete Zeilen (gebuchtAm) tragen ein Schloss
 * und keinen Loesch-Knopf — sie sind Beleg.
 */
export const TaetigkeitenListe: React.FC<Props> = ({ taetigkeiten, laden, jahr, isReadOnly = false, isMobile = false, onBearbeiten, onLoeschen }) => {
  const [suche, setSuche] = useState('');
  const sichtbar = sucheTaetigkeiten(taetigkeiten, suche);

  const aktionen = (t: SifaTaetigkeit) => (
    <div className="flex items-center gap-1">
      <button onClick={() => onBearbeiten(t)} className="min-h-[36px] px-2.5 flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg">
        <PencilLine className="w-3.5 h-3.5" /> {isReadOnly || t.gebuchtAm ? 'Ansehen' : 'Bearbeiten'}
      </button>
      {!isReadOnly && (darfLoeschen(t) ? (
        <button onClick={() => onLoeschen(t)} className="min-h-[36px] min-w-[36px] flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg" title="Löschen">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      ) : (
        <span className="min-h-[36px] min-w-[36px] flex items-center justify-center text-slate-400" title={`Abgerechnet am ${formatDatum(t.gebuchtAm)} — nicht löschbar`}>
          <Lock className="w-3.5 h-3.5" />
        </span>
      ))}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Titel, Art, Bezug, Wer…" className="w-full pl-9 pr-3 py-2 min-h-[40px] text-sm bg-white border border-slate-200 rounded-xl outline-none focus:ring-1 focus:ring-indigo-300" />
        </div>
        <span className="text-xs text-slate-500">{sichtbar.length} von {taetigkeiten.length}</span>
      </div>

      {laden ? (
        <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>
      ) : sichtbar.length === 0 ? (
        <div className="p-8 bg-white border border-dashed border-slate-300 rounded-2xl text-center">
          <p className="text-sm text-slate-600 font-semibold">{taetigkeiten.length === 0 ? `Noch keine Tätigkeit für ${jahr}` : 'Kein Treffer'}</p>
          {taetigkeiten.length === 0 && (
            <p className="text-xs text-slate-500 mt-1">Begehungen, Beratung, ASA, Unterweisungen — jede Zeile zählt gegen die Soll-Einsatzzeit und steht im Tätigkeitsnachweis.</p>
          )}
        </div>
      ) : isMobile ? (
        <div className="space-y-2">
          {sichtbar.map((t) => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-2xl p-3 space-y-1.5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-800 truncate">{t.titel}</p>
                  <p className="text-xs text-slate-500 truncate">{formatDatum(t.datum)} · {artLabel(t.art)}{t.durchgefuehrtVon ? ` · ${t.durchgefuehrtVon}` : ''}</p>
                </div>
                <span className="text-sm font-bold text-indigo-700 whitespace-nowrap">{formatDauer(t.dauerMinuten)}</span>
              </div>
              <div className="flex items-center justify-end gap-2">{aktionen(t)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="px-3 py-2 text-left font-bold">Datum</th>
                <th className="px-3 py-2 text-left font-bold">Art</th>
                <th className="px-3 py-2 text-left font-bold">Titel</th>
                <th className="px-3 py-2 text-right font-bold">Dauer</th>
                <th className="px-3 py-2 text-left font-bold">Wer</th>
                <th className="px-3 py-2 text-left font-bold">Bezug</th>
                <th className="px-3 py-2 text-left font-bold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sichtbar.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50/60">
                  <td className="px-3 py-2 whitespace-nowrap font-semibold text-slate-700">{formatDatum(t.datum)}</td>
                  <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{artLabel(t.art)}</td>
                  <td className="px-3 py-2">
                    <p className="font-semibold text-slate-800">{t.titel}</p>
                    {t.beschreibung && <p className="text-xs text-slate-500 truncate max-w-[360px]" title={t.beschreibung}>{t.beschreibung}</p>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap font-mono text-slate-700">{formatDauer(t.dauerMinuten)}</td>
                  <td className="px-3 py-2 text-slate-600">{t.durchgefuehrtVon || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2 text-slate-600">{t.bezug || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2">{aktionen(t)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
